/**
 * ContactNoteEditorDialog
 * -----------------------------------------------------------------------------
 * Modal de criação/edição de uma anotação do contato. Replica o padrão visual
 * do `DealDescriptionAndAttachments`: editor de texto rico (RichTextEditor),
 * área de upload e grade de anexos (AttachmentThumbnail) com preview via
 * ImageLightbox.
 *
 * - Criação:  passa `note=null` e `contactId` (cria a nota antes do upload).
 * - Edição:   passa `note` carregada (id obrigatório).
 * - Readonly: passa `readOnly` quando o usuário não é autor nem admin.
 *
 * @version 1.1.0
 * RELEASE: 2026-06-03 v1.1.0 - Layout 2 colunas com Timeline de eventos + botão
 *                              "Marcar como lida" no rodapé.
 * RELEASE: 2026-06-03 v1.0.1 - Preview interno para todos os tipos suportados (image/video/document) via ImageLightbox; fallback download via blob para tipos sem preview.
 * RELEASE: 2026-06-03 v1.0.0 - Criação inicial.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCheck, Eye, Loader2, MessageSquare, Paperclip, RotateCcw, Save, Sparkles, StickyNote, X } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { AttachmentUploadArea } from '@/components/shared/AttachmentUploadArea';
import { AttachmentThumbnail } from '@/components/shared/AttachmentThumbnail';
import { ImageLightbox, MediaItem } from '@/components/tenant/chat/ImageLightbox';
import { FormSectionCard } from '@/components/crm/deals/components/FormSectionCard';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { sanitizeHtml } from '@/lib/sanitize';
import { logger } from '@/lib/logger';
import {
  useContactNoteAttachments,
  ContactNoteAttachment,
} from '@/hooks/useContactNoteAttachments';
import { ContactNoteTimeline } from './ContactNoteTimeline';
import { Badge } from '@/components/ui/badge';

export interface ContactNoteEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Quando preenchido, está em modo edição. */
  noteId?: string | null;
  /** Obrigatório para criação (modo novo). */
  contactId: string;
  /** Bloqueia edição (usuário sem permissão). */
  readOnly?: boolean;
  /** Callback após salvar/criar. */
  onSaved?: (noteId: string) => void;
}

interface NoteData {
  id: string;
  contact_id: string;
  content_html: string;
  content_plain: string;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export function ContactNoteEditorDialog({
  open,
  onOpenChange,
  noteId,
  contactId,
  readOnly = false,
  onSaved,
}: ContactNoteEditorDialogProps) {
  const { currentTenant } = useAuth();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estado local
  const [contentHtml, setContentHtml] = useState('');
  const [isLoadingNote, setIsLoadingNote] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeNoteId, setActiveNoteId] = useState<string | null>(noteId ?? null);
  const [lightbox, setLightbox] = useState<{
    open: boolean;
    index: number;
    items: MediaItem[];
  }>({ open: false, index: 0, items: [] });

  const isEditMode = !!noteId;

  // Carrega nota existente.
  useEffect(() => {
    if (!open) return;
    setActiveNoteId(noteId ?? null);
    if (!noteId) {
      setContentHtml('');
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoadingNote(true);
      try {
        const { data, error } = await supabase.rpc('get_contact_note', {
          p_note_id: noteId,
          p_tenant_hint: currentTenant?.id ?? null,
        } as never);
        if (error) throw error;
        if (cancelled) return;
        const note = data as unknown as NoteData;
        // (cast acima já trata o tipo unknown vindo do RPC)
        setContentHtml(note?.content_html ?? '');
      } catch (err: unknown) {
        logger.error('ContactNoteEditorDialog', `load note: ${String(err)}`);
        toast.error('Erro ao carregar anotação');
      } finally {
        if (!cancelled) setIsLoadingNote(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, noteId, currentTenant?.id]);

  // Hook de anexos — só funciona depois que a nota existe (tem id).
  const {
    attachments,
    uploadFile,
    deleteAttachment,
    isUploading,
    isDeleting,
    canAddMore,
  } = useContactNoteAttachments(activeNoteId ?? undefined);

  // -------------------------------------------------------------------------
  // Status de leitura — busca via list_contact_notes (consistente com a lista)
  // -------------------------------------------------------------------------
  const { data: readStatus, refetch: refetchReadStatus } = useQuery({
    queryKey: ['contact-note-read-status', activeNoteId, currentTenant?.id],
    enabled: !!activeNoteId && !!contactId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_contact_notes', {
        p_contact_id: contactId,
        p_tenant_hint: currentTenant?.id ?? null,
      } as never);
      if (error) throw error;
      const row = (data as Array<{
        id: string;
        read_count: number;
        is_read_by_me: boolean;
      }>)?.find((r) => r.id === activeNoteId);
      return row
        ? { read_count: row.read_count, is_read_by_me: row.is_read_by_me }
        : { read_count: 0, is_read_by_me: false };
    },
  });

  const [isMarkingRead, setIsMarkingRead] = useState(false);

  /** Marca a anotação como lida pelo usuário atual. */
  const handleMarkRead = async () => {
    if (!activeNoteId) return;
    setIsMarkingRead(true);
    try {
      const { error } = await supabase.rpc('mark_contact_note_read', {
        p_note_id: activeNoteId,
        p_tenant_hint: currentTenant?.id ?? null,
      } as never);
      if (error) throw error;
      toast.success('Anotação marcada como lida');
      await refetchReadStatus();
      qc.invalidateQueries({ queryKey: ['contact-note-events', activeNoteId] });
      qc.invalidateQueries({ queryKey: ['contact-notes', contactId] });
      qc.invalidateQueries({ queryKey: ['contact-notes-count', contactId] });
    } catch (err) {
      logger.error('ContactNoteEditorDialog', `mark read: ${String(err)}`);
      toast.error('Não foi possível marcar como lida');
    } finally {
      setIsMarkingRead(false);
    }
  };

  /** Reabre a anotação (desmarca leitura do usuário atual). */
  const handleReopen = async () => {
    if (!activeNoteId) return;
    setIsMarkingRead(true);
    try {
      const { error } = await supabase.rpc('unmark_contact_note_read', {
        p_note_id: activeNoteId,
        p_tenant_hint: currentTenant?.id ?? null,
      } as never);
      if (error) throw error;
      toast.success('Anotação reaberta');
      await refetchReadStatus();
      qc.invalidateQueries({ queryKey: ['contact-note-events', activeNoteId] });
      qc.invalidateQueries({ queryKey: ['contact-notes', contactId] });
      qc.invalidateQueries({ queryKey: ['contact-notes-count', contactId] });
    } catch (err) {
      logger.error('ContactNoteEditorDialog', `reopen: ${String(err)}`);
      toast.error('Não foi possível reabrir');
    } finally {
      setIsMarkingRead(false);
    }
  };

  /** Salva (cria se necessário) e retorna o id da nota. */
  const persistNote = async (): Promise<string | null> => {
    setIsSaving(true);
    try {
      const safeHtml = sanitizeHtml(contentHtml || '');
      // Extrai texto puro para coluna de busca/preview.
      const plain = safeHtml
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      const { data, error } = await supabase.rpc('upsert_contact_note', {
        p_note_id: activeNoteId,
        p_contact_id: contactId,
        p_content_html: safeHtml,
        p_content_plain: plain,
        p_tenant_hint: currentTenant?.id ?? null,
      } as never);
      if (error) throw error;
      const newId = data as unknown as string;
      setActiveNoteId(newId);
      qc.invalidateQueries({ queryKey: ['contact-notes', contactId] });
      qc.invalidateQueries({ queryKey: ['contact-notes-count', contactId] });
      return newId;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar';
      logger.error('ContactNoteEditorDialog', `save: ${String(err)}`);
      toast.error(msg);
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  /** Salvar manual (botão). */
  const handleSave = async () => {
    const id = await persistNote();
    if (id) {
      toast.success(isEditMode ? 'Anotação atualizada' : 'Anotação criada');
      onSaved?.(id);
    }
  };

  /** Antes de anexar — garante que existe id. */
  const handlePickFile = async () => {
    if (readOnly) return;
    if (!activeNoteId) {
      const id = await persistNote();
      if (!id) return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const file of files) {
      try {
        await uploadFile(file);
      } catch {
        // toast tratado no hook
      }
    }
  };

  // Tipos previsualizáveis pelo ImageLightbox (image/video/document via iframe).
  const PREVIEWABLE_TYPES = ['image', 'video', 'document', 'pdf'] as const;

  /** Mapeia o file_type do anexo para o tipo aceito pelo MediaItem. */
  const toMediaType = (fileType: string): MediaItem['type'] | null => {
    if (fileType === 'image') return 'image';
    if (fileType === 'video') return 'video';
    if (fileType === 'document' || fileType === 'pdf') return 'document';
    return null;
  };

  const previewableAttachments = useMemo(
    () => attachments.filter((a) => toMediaType(a.file_type) !== null),
    [attachments],
  );

  /** Fallback: download via blob (evita abrir aba externa para tipos sem preview). */
  const downloadViaBlob = async (url: string, filename: string) => {
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error('download_failed');
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Não foi possível baixar o arquivo');
    }
  };

  const openLightboxFor = (att: ContactNoteAttachment) => {
    const mediaType = toMediaType(att.file_type);
    if (!mediaType) {
      // Tipo sem preview interno (ex.: .zip) → baixa via blob, sem abrir aba externa.
      void downloadViaBlob(att.public_url, att.file_name);
      return;
    }
    const items: MediaItem[] = previewableAttachments.map((a) => ({
      url: a.public_url,
      type: toMediaType(a.file_type) ?? 'document',
      title: a.file_name,
    }));
    const idx = previewableAttachments.findIndex((a) => a.id === att.id);
    setLightbox({ open: true, index: Math.max(0, idx), items });
  };

  const handleClose = (next: boolean) => {
    if (!next && isSaving) return;
    onOpenChange(next);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          className="sm:max-w-4xl p-0 gap-0 overflow-hidden max-h-[90vh] h-[90dvh] sm:h-auto flex flex-col"
          hideCloseButton={false}
        >
          {/* ===== HEADER PREMIUM (padrão ScheduleMessageDialog) ===== */}
          <div className="px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
            <DialogHeader>
              <div className="flex items-center justify-between gap-2">
                <DialogTitle className="flex items-center gap-2 text-base">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                    <StickyNote className="h-3.5 w-3.5 text-primary" />
                  </div>
                  {isEditMode ? 'Editar anotação' : 'Nova anotação'}
                </DialogTitle>
                {activeNoteId && readStatus && readStatus.read_count > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    <CheckCheck className="h-3 w-3" />
                    Lida por {readStatus.read_count}
                  </Badge>
                )}
              </div>
              <DialogDescription className="text-xs text-muted-foreground">
                Registre observações sobre o contato. Toda alteração é
                rastreada na timeline.
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* ===== CORPO SPLIT-PANE ===== */}
          {isLoadingNote ? (
            <div className="flex-1 flex items-center justify-center gap-2 text-muted-foreground py-10">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando...
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr,340px] min-h-0 flex-1">
              {/* Painel esquerdo — formulário */}
              <ScrollArea className="max-h-[calc(90vh-180px)]">
                <div className="p-5 space-y-4">
                  <FormSectionCard title="Conteúdo" icon={MessageSquare}>
                    <div className="rounded-md border bg-background">
                      <RichTextEditor
                        content={contentHtml}
                        onChange={setContentHtml}
                        placeholder="Escreva a anotação..."
                        readOnly={readOnly}
                        minHeight="200px"
                      />
                    </div>
                  </FormSectionCard>

                  <FormSectionCard title={`Anexos (${attachments.length})`} icon={Paperclip}>
                    {attachments.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {attachments.map((att) => (
                          <AttachmentThumbnail
                            key={att.id}
                            attachment={att}
                            readOnly={readOnly}
                            onClick={() => openLightboxFor(att)}
                            onDelete={() => deleteAttachment(att.id)}
                          />
                        ))}
                      </div>
                    )}

                    {!readOnly && canAddMore && (
                      <AttachmentUploadArea
                        onUploadClick={handlePickFile}
                        isUploading={isUploading}
                      />
                    )}

                    {!readOnly && (
                      <p className="text-[11px] text-muted-foreground">
                        Máx. 10MB por arquivo.
                      </p>
                    )}

                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFileChange}
                      accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                    />
                  </FormSectionCard>
                </div>
              </ScrollArea>

              {/* Painel direito — timeline */}
              <div className="border-t lg:border-t-0 lg:border-l flex flex-col min-h-0 bg-muted/10">
                <div className="flex items-center gap-2 px-4 py-3 border-b bg-background/80 backdrop-blur-sm flex-shrink-0">
                  <Eye className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs font-medium">Timeline de eventos</span>
                </div>
                <ScrollArea className="flex-1">
                  <div className="p-4">
                    <ContactNoteTimeline noteId={activeNoteId} />
                  </div>
                </ScrollArea>
              </div>
            </div>
          )}

          {/* ===== FOOTER (padrão ScheduleMessageDialog) ===== */}
          <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10">
            {activeNoteId && (
              <Button
                variant={readStatus?.is_read_by_me ? 'secondary' : 'outline'}
                size="sm"
                className="h-7 px-2 sm:px-3"
                onClick={readStatus?.is_read_by_me ? handleReopen : handleMarkRead}
                disabled={isMarkingRead}
                title={
                  readStatus?.is_read_by_me
                    ? 'Reabrir anotação (volta a contar como pendente)'
                    : 'Marcar como lida'
                }
              >
                {isMarkingRead ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : readStatus?.is_read_by_me ? (
                  <RotateCcw className="h-4 w-4 mr-1" />
                ) : (
                  <CheckCheck className="h-4 w-4 mr-1" />
                )}
                {readStatus?.is_read_by_me ? 'Reabrir anotação' : 'Marcar como lida'}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 sm:px-3"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              <X className="h-4 w-4 mr-1" /> Fechar
            </Button>
            {!readOnly && (
              <Button
                size="sm"
                className="h-7 px-2 sm:px-3"
                onClick={handleSave}
                disabled={isSaving || isLoadingNote}
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Salvar
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ImageLightbox
        mediaItems={lightbox.items}
        initialIndex={lightbox.index}
        open={lightbox.open}
        onClose={() => setLightbox((s) => ({ ...s, open: false }))}
      />

      {/* indicador de exclusão (overlay simples — tratado por toast) */}
      {isDeleting && (
        <div className="sr-only" aria-live="polite">
          Excluindo anexo...
        </div>
      )}
    </>
  );
}
